import type { Locale } from "./locale";
import { ko, type MessageCatalog, type MessageKey } from "./messages/ko";
import { en } from "./messages/en";

export type MessageParams = Record<string, string | number>;

/** 렌더 시점에 번역할 문구. 언어를 바꿔도 남아 있는 상태 문구는 문자열 대신 이것을 든다. */
export interface MessageDescriptor {
  readonly key: MessageKey;
  readonly params?: MessageParams;
}

export type Localize = (key: MessageKey, params?: MessageParams) => string;

type PluralBaseOf<Key> = Key extends `${infer Base}.other` ? Base : never;
/** `.one` / `.other` 변형을 가진 키의 공통 접두 — `tn("common.itemCount", 3)` 처럼 쓴다. */
export type PluralMessageKey = PluralBaseOf<MessageKey>;

const catalogs: Record<Locale, MessageCatalog> = { ko, en };
const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;
const pluralRulesByLocale = new Map<Locale, Intl.PluralRules>();

export function translate(
  locale: Locale,
  key: MessageKey,
  params?: MessageParams
): string {
  return formatMessage(catalogs[locale][key], params);
}

/**
 * 로케일의 복수 규칙(`Intl.PluralRules`)으로 `{base}.{category}` 변형을 고르고 `count` 를 보간한다.
 * 한국어는 항상 `other` 하나이고, 영어는 `one`/`other` 둘을 사전에 둔다. 사전에 없는 범주는 `other` 로 간다.
 */
export function translatePlural(
  locale: Locale,
  baseKey: PluralMessageKey,
  count: number,
  params?: MessageParams
): string {
  const category = pluralRulesFor(locale).select(count);
  const categoryKey = `${baseKey}.${category}` as MessageKey;
  const resolvedKey =
    categoryKey in catalogs[locale]
      ? categoryKey
      : (`${baseKey}.other` as MessageKey);
  return translate(locale, resolvedKey, { count, ...params });
}

function pluralRulesFor(locale: Locale): Intl.PluralRules {
  const cached = pluralRulesByLocale.get(locale);
  if (cached) return cached;
  const rules = new Intl.PluralRules(locale);
  pluralRulesByLocale.set(locale, rules);
  return rules;
}

/**
 * `{name}` 자리표시자를 params 값으로 치환한다. 이름이 없는 자리표시자는 그대로 남겨
 * 누락이 화면에서 바로 보이게 한다.
 */
export function formatMessage(
  template: string,
  params?: MessageParams
): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER_PATTERN, (placeholder, name: string) =>
    name in params ? String(params[name]) : placeholder
  );
}
